export interface TemplateMetadata {
  id: string
  name: string
  description: string
  category: 'professional' | 'creative' | 'modern' | 'classic' | 'colorful'
  colors: {
    primary: string
    secondary: string
    gradient?: string
  }
  popular?: boolean
  preview: {
    gradientClass: string
    accentClass: string
  }
}

export const TEMPLATE_REGISTRY: Record<string, TemplateMetadata> = {
  modern: {
    id: 'modern',
    name: 'Modern Professional',
    description: 'Clean gradient design for tech and business',
    category: 'modern',
    colors: {
      primary: '#2563eb',
      secondary: '#1d4ed8',
      gradient: 'from-blue-600 to-blue-700'
    },
    popular: true,
    preview: {
      gradientClass: 'from-blue-500 via-indigo-500 to-purple-600',
      accentClass: 'border-blue-500 bg-blue-50'
    }
  },

  traditional: {
    id: 'traditional',
    name: 'Traditional Classic',
    description: 'Timeless black & white professional',
    category: 'classic',
    colors: {
      primary: '#111827',
      secondary: '#374151',
      gradient: 'from-gray-800 to-gray-900'
    },
    preview: {
      gradientClass: 'from-gray-700 via-gray-800 to-black',
      accentClass: 'border-gray-700 bg-gray-50'
    }
  },

  minimal: {
    id: 'minimal',
    name: 'Minimal Clean',
    description: 'Ultra-clean minimalist design',
    category: 'classic',
    colors: {
      primary: '#1f2937',
      secondary: '#4b5563',
      gradient: 'from-gray-600 to-gray-700'
    },
    preview: {
      gradientClass: 'from-gray-400 via-gray-500 to-gray-600',
      accentClass: 'border-gray-400 bg-gray-50'
    }
  },

  executive: {
    id: 'executive',
    name: 'Executive Elite',
    description: 'Sophisticated design for senior roles',
    category: 'professional',
    colors: {
      primary: '#1e293b',
      secondary: '#334155',
      gradient: 'from-slate-700 to-slate-800'
    },
    popular: true,
    preview: {
      gradientClass: 'from-gray-700 via-gray-800 to-black',
      accentClass: 'border-gray-700 bg-gray-50'
    }
  },

  technical: {
    id: 'technical',
    name: 'Technical Pro',
    description: 'Tech-focused with green accents',
    category: 'modern',
    colors: {
      primary: '#059669',
      secondary: '#047857',
      gradient: 'from-green-600 to-teal-600'
    },
    preview: {
      gradientClass: 'from-green-500 via-teal-600 to-blue-700',
      accentClass: 'border-green-500 bg-green-50'
    }
  },

  creative: {
    id: 'creative',
    name: 'Creative Edge',
    description: 'Bold design for creative professionals',
    category: 'creative',
    colors: {
      primary: '#9333ea',
      secondary: '#7e22ce',
      gradient: 'from-purple-600 to-pink-600'
    },
    popular: true,
    preview: {
      gradientClass: 'from-pink-500 via-purple-500 to-indigo-600',
      accentClass: 'border-purple-500 bg-purple-50'
    }
  },

  'bold-blue': {
    id: 'bold-blue',
    name: 'Bold Blue',
    description: 'Striking blue design',
    category: 'colorful',
    colors: {
      primary: '#1e40af',
      secondary: '#1e3a8a',
      gradient: 'from-blue-700 to-blue-800'
    },
    preview: {
      gradientClass: 'from-blue-600 via-blue-700 to-blue-800',
      accentClass: 'border-blue-600 bg-blue-50'
    }
  },

  'coral-pink': {
    id: 'coral-pink',
    name: 'Coral Pink',
    description: 'Warm coral and pink tones',
    category: 'colorful',
    colors: {
      primary: '#f43f5e',
      secondary: '#e11d48',
      gradient: 'from-rose-500 to-pink-600'
    },
    preview: {
      gradientClass: 'from-rose-400 via-pink-500 to-rose-600',
      accentClass: 'border-rose-500 bg-rose-50'
    }
  },

  'navy-gold': {
    id: 'navy-gold',
    name: 'Navy Gold',
    description: 'Elegant navy with gold highlights',
    category: 'professional',
    colors: {
      primary: '#1e3a8a',
      secondary: '#f59e0b',
      gradient: 'from-blue-900 to-blue-950'
    },
    preview: {
      gradientClass: 'from-blue-900 via-blue-800 to-blue-950',
      accentClass: 'border-blue-900 bg-blue-50'
    }
  },

  'lime-green': {
    id: 'lime-green',
    name: 'Lime Green',
    description: 'Fresh lime green energy',
    category: 'colorful',
    colors: {
      primary: '#65a30d',
      secondary: '#4d7c0f',
      gradient: 'from-lime-600 to-lime-700'
    },
    preview: {
      gradientClass: 'from-lime-500 via-lime-600 to-green-700',
      accentClass: 'border-lime-600 bg-lime-50'
    }
  },

  'crimson-red': {
    id: 'crimson-red',
    name: 'Crimson Red',
    description: 'Bold crimson statement',
    category: 'colorful',
    colors: {
      primary: '#dc2626',
      secondary: '#b91c1c',
      gradient: 'from-red-600 to-red-700'
    },
    preview: {
      gradientClass: 'from-red-500 via-red-600 to-red-700',
      accentClass: 'border-red-600 bg-red-50'
    }
  },

  aspire: {
    id: 'aspire',
    name: 'Aspire Purple',
    description: 'Inspiring purple gradient',
    category: 'creative',
    colors: {
      primary: '#7c3aed',
      secondary: '#6d28d9',
      gradient: 'from-purple-600 to-violet-700'
    },
    preview: {
      gradientClass: 'from-purple-500 via-violet-600 to-purple-700',
      accentClass: 'border-purple-600 bg-purple-50'
    }
  },

  functional: {
    id: 'functional',
    name: 'Functional Teal',
    description: 'Skills-focused teal design',
    category: 'modern',
    colors: {
      primary: '#0d9488',
      secondary: '#0f766e',
      gradient: 'from-teal-600 to-teal-700'
    },
    preview: {
      gradientClass: 'from-teal-500 via-cyan-600 to-teal-700',
      accentClass: 'border-teal-600 bg-teal-50'
    }
  },

  combined: {
    id: 'combined',
    name: 'Combined Orange',
    description: 'Balanced orange gradient',
    category: 'modern',
    colors: {
      primary: '#ea580c',
      secondary: '#c2410c',
      gradient: 'from-orange-600 to-orange-700'
    },
    preview: {
      gradientClass: 'from-orange-500 via-amber-600 to-orange-700',
      accentClass: 'border-orange-600 bg-orange-50'
    }
  },

  hybrid: {
    id: 'hybrid',
    name: 'Hybrid Pink',
    description: 'Versatile pink gradient',
    category: 'creative',
    colors: {
      primary: '#ec4899',
      secondary: '#db2777',
      gradient: 'from-pink-600 to-pink-700'
    },
    preview: {
      gradientClass: 'from-pink-500 via-rose-600 to-pink-700',
      accentClass: 'border-pink-600 bg-pink-50'
    }
  }
}

export const getTemplatesByCategory = (category: string) => {
  return Object.values(TEMPLATE_REGISTRY).filter(t => t.category === category)
}

export const getPopularTemplates = () => {
  return Object.values(TEMPLATE_REGISTRY).filter(t => t.popular)
}

export const getAllTemplates = () => {
  return Object.values(TEMPLATE_REGISTRY)
}
